// src/contexts/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("app-theme");
    return savedTheme || "dark";
  });

  // Get initial density from localStorage or default to 'standard'
  const [density, setDensity] = useState(() => {
    const savedDensity = localStorage.getItem("app-density");
    return savedDensity || "standard";
  });

  // Save theme to localStorage and update DOM classes whenever it changes
  useEffect(() => {
    localStorage.setItem("app-theme", theme);
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      root.style.setProperty("color-scheme", "dark");
      document.body.style.backgroundColor = "#09090b";
    } else {
      root.classList.remove("dark");
      root.style.setProperty("color-scheme", "light");
      document.body.style.backgroundColor = "#f8fafc";
    }
  }, [theme]);

  // Update density class on document element
  useEffect(() => {
    localStorage.setItem("app-density", density);
    const root = document.documentElement;
    root.classList.remove("density-standard", "density-compact");
    root.classList.add(`density-${density}`);
  }, [density]);

  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === "light" ? "dark" : "light"));
  };

  const toggleDensity = () => {
    setDensity((prev) => (prev === "standard" ? "compact" : "standard"));
  };

  // Custom Theme Override (currently disabled — see merge below).
  // Also clear any previously saved override from localStorage so stale dark
  // values don't bleed into light mode when the feature returns later.
  const [customThemeConfig, setCustomThemeConfig] = useState({});
  useEffect(() => {
    try {
      localStorage.removeItem("custom-theme-config");
    } catch (e) {
      // ignore
    }
  }, []);

  const saveThemeConfig = (newConfig) => {
    setCustomThemeConfig(newConfig);
    localStorage.setItem("custom-theme-config", JSON.stringify(newConfig));
  };

  const resetTheme = () => {
    setCustomThemeConfig({});
    localStorage.removeItem("custom-theme-config");
  };

  // Enterprise Grade Theme Configuration (Solid, High Contrast, No Blur)
  const themes = {
    dark: {
      // 1. BACKGROUNDS - Deep, Minimalist Zinc Palette
      bgPrimary: "bg-[#09090b]",   // Zinc-950: Screen background
      bgSecondary: "bg-[#09090b]", // Usually same as primary in modern dark apps
      bgTertiary: "bg-[#18181b]",  // Zinc-900: Used for nested contrast
      bgHover: "hover:bg-[#18181b]", // Subtle hover
      bgHoverStrong: "hover:bg-[#27272a]", // More defined hover
      bgActive: "bg-[#27272a]",

      // 2. BORDERS - Clean & Transparent
      border: "border-[#27272a]",         // Zinc-800
      borderSecondary: "border-white/5",  // Ultra-subtle inner borders

      // 3. TYPOGRAPHY - Highly Legible Zinc Colors
      textPrimary: "text-[#fafafa]",    // Zinc-50: Titles/Primary
      textSecondary: "text-[#a1a1aa]",  // Zinc-400: Body/Secondary
      textTertiary: "text-[#71717a]",   // Zinc-500: Meta/Captions
      textMuted: "text-[#52525b]",      // Zinc-600: Placeholder/Details

      // 4. COMPONENTS - Professional Card Design
      cardBg: "bg-[#09090b]",           // Deep cards
      cardBorder: "border-[#27272a]",   // Clean boundaries
      tableHeader: "bg-[#09090b]",      // Unified with background
      tableRowHover: "hover:bg-[#18181b]",
      tableDivide: "divide-[#27272a]",
      tableBorder: "border-[#27272a]",

      // 5. INPUTS - Low Profile, High Contrast Focus
      inputBg: "bg-[#18181b]",       
      inputBorder: "border-[#3f3f46]", // Zinc-700 for high visibility
      inputText: "text-[#fafafa]",
      inputPlaceholder: "placeholder-[#52525b]",
      inputFocus: "focus:ring-2 focus:ring-indigo-600/50 focus:border-indigo-500",

      // 6. UI ELEMENTS
      paginationBg: "bg-[#09090b]",
      paginationText: "text-[#a1a1aa]",
      paginationHover: "hover:bg-[#18181b] hover:text-[#fafafa]",
      
      emptyStateBg: "bg-[#09090b]",
      emptyStateIcon: "text-[#27272a]",

      // 7. BUTTONS & ACTIONS
      btnSecondaryBg: "bg-[#18181b]",
      btnSecondaryBorder: "border-[#27272a]",
      btnSecondaryText: "text-[#fafafa]",
      btnSecondaryHover: "hover:bg-[#27272a]",

      // 8. SIDEBAR - Distinct Navigation
      sidebarBg: "bg-[#09090b]",
      sidebarBorder: "border-r border-[#18181b]",
    },
    light: {
      // Backgrounds - Clean & Bright
      bgPrimary: "bg-[#f8fafc]", // Slate-50
      bgSecondary: "bg-[#ffffff]", // White
      bgTertiary: "bg-[#f1f5f9]", // Slate-100

      // Interactions
      bgHover: "hover:bg-[#f1f5f9]", // Slate-100
      bgHoverStrong: "hover:bg-[#e2e8f0]", // Slate-200
      bgActive: "bg-[#f1f5f9]",

      // Borders
      border: "border-[#e2e8f0]", // Slate-200
      borderSecondary: "border-[#cbd5e1]", // Slate-300

      // Typography
      textPrimary: "text-[#0f172a]", // Slate-900
      textSecondary: "text-[#475569]", // Slate-600
      textTertiary: "text-[#64748b]", // Slate-500
      textMuted: "text-[#94a3b8]", // Slate-400

      // Inputs
      inputBg: "bg-[#f8fafc]",     
      inputBorder: "border-[#94a3b8]", // Slate-400 for high visibility
      inputText: "text-[#0f172a]",
      inputPlaceholder: "placeholder-[#94a3b8]",
      inputFocus: "focus:ring-2 focus:ring-indigo-600 focus:border-transparent",

      // Components
      cardBg: "bg-[#ffffff]",
      cardBorder: "border-[#e2e8f0]",

      // Data Display
      tableHeader: "bg-[#f8fafc]",
      tableRowHover: "hover:bg-[#f1f5f9]",
      tableDivide: "divide-[#e2e8f0]",
      tableBorder: "border-[#e2e8f0]",

      // UI Elements
      paginationBg: "bg-[#ffffff]",
      paginationText: "text-[#475569]",
      paginationHover: "hover:bg-[#f1f5f9] hover:text-[#0f172a]",

      emptyStateBg: "bg-[#f8fafc]",
      emptyStateIcon: "text-[#cbd5e1]",

      // Buttons
      btnSecondaryBg: "bg-[#ffffff]",
      btnSecondaryBorder: "border-[#e2e8f0]",
      btnSecondaryText: "text-[#475569]",
      btnSecondaryHover: "hover:bg-[#f8fafc]",

      // Sidebar specific
      sidebarBg: "bg-[#ffffff]",
      sidebarBorder: "border-[#e2e8f0]",
    },
  };

  // The "Customize Theme" UI is currently hidden (see SettingsPage). The
  // previous implementation merged a per-user customThemeConfig on top of the
  // active theme — but the same overrides were applied to BOTH dark and light
  // modes, so values picked while in dark mode bled into light mode and made
  // the page look broken (light containers with dark backgrounds).
  // Until that UI returns with proper per-mode storage, we ignore the saved
  // overrides entirely and just use the base theme.
  const activeBaseTheme = themes[theme];
  const colors = activeBaseTheme;

  const value = {
    theme,
    toggleTheme,
    density,
    setDensity,
    toggleDensity,
    isDark: theme === "dark",
    isLight: theme === "light",
    colors,
    saveThemeConfig,
    resetTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export default ThemeContext;
